/**
 * Complaint Filter State Hook
 *
 * 4-Section Hybrid Filter Architecture:
 * SECTION 1: COMPLAINT STATUS & SEVERITY (Tier 1 - SQL filters)
 *   - status: open, in_progress, closed, cancelled
 *   - severity: critical, major, minor
 *
 * SECTION 2: DEVICE & DEPARTMENT (Tier 1 - SQL filters)
 *   - devices: Device IDs
 *   - departments: Department names
 *
 * SECTION 3: DATES (Tier 2 - Date-based calculations)
 *   - createdDateFrom / createdDateTo — only when user sets Advanced Filters
 *   - resolutionDateFrom / resolutionDateTo
 *   - daysOpenFrom / daysOpenTo
 *
 * SECTION 4: SEARCH (Tier 3 - Text search)
 *   - search: Complaint ID, title, details, device name, reporter name
 *
 * Server default (when created dates are omitted): open tickets any age,
 * plus closed/cancelled created in the last 30 days. Not represented as UI filter state.
 */

import { useState } from 'react';

/** Matches backend COMPLAINTS_DEFAULT_LOOKBACK_DAYS (banner copy only). */
export const COMPLAINTS_DEFAULT_LOOKBACK_DAYS = 30;

export const createInitialComplaintFilterState = () => ({
  // SECTION 1: COMPLAINT STATUS & SEVERITY
  // Default: Empty array = show all statuses (backend treats empty array as "no filter")
  statuses: [],
  severities: [],         // ['critical', 'major', 'minor']
  impactsAvailability: '', // '', 'yes', 'no', 'pending'
  filedByMe: false,       // Quick filter: complaints reported by the current user

  // SECTION 1.5: LOCATION (NEW - For superadmin/admin cross-location visibility)
  locations: [],          // Location IDs to filter by (empty = use all accessible locations)

  // SECTION 2: DEVICE & DEPARTMENT
  deviceIds: [],          // Device IDs to filter by
  departments: [],        // Department names to filter by
  categoryIds: [],        // Complaint category IDs to filter by

  // SECTION 3: DATES — empty means server default window (not all-time)
  createdDateFrom: '',    // ISO format: YYYY-MM-DD
  createdDateTo: '',      // ISO format: YYYY-MM-DD
  resolutionDateFrom: '', // ISO format: YYYY-MM-DD
  resolutionDateTo: '',   // ISO format: YYYY-MM-DD
  daysOpenFrom: '',       // Numeric: minimum days complaint is open
  daysOpenTo: '',         // Numeric: maximum days complaint is open

  // SECTION 4: SEARCH
  search: '',             // Text search across multiple fields
});

/** List-page defaults: preference statuses only (date window is server-side). */
export const createDefaultComplaintListFilterState = (statuses = []) => ({
  ...createInitialComplaintFilterState(),
  statuses: Array.isArray(statuses) ? [...statuses] : [],
});

/** True when the server default created-date window applies (banner only). */
export const isComplaintsServerDateWindowActive = (filters) => (
  !filters?.createdDateFrom
  && !filters?.createdDateTo
  && !filters?.resolutionDateFrom
  && !filters?.resolutionDateTo
  && (filters?.daysOpenFrom === '' || filters?.daysOpenFrom == null)
  && (filters?.daysOpenTo === '' || filters?.daysOpenTo == null)
);

/**
 * Hook for managing complaint filter state
 *
 * @returns {[Object, Function]} Filter state and setter function
 */
export const useComplaintFilterState = () => {
  const [filters, setFilters] = useState(createInitialComplaintFilterState());
  return [filters, setFilters];
};

/**
 * Reset filters to initial empty state
 */
export const getEmptyComplaintFilters = () => {
  return createInitialComplaintFilterState();
};

/**
 * Check if any complaint filters are currently active
 *
 * @param {Object} filters - Filter object to check
 * @returns {boolean} True if any non-default filters are set
 */
export const hasActiveComplaintFilters = (filters) => {
  if (!filters) return false;

  return !!(
    (filters.statuses && filters.statuses.length > 0) ||
    (filters.severities && filters.severities.length > 0) ||
    (filters.impactsAvailability && filters.impactsAvailability !== '') ||
    Boolean(filters.filedByMe) ||
    (filters.locations && filters.locations.length > 0) ||
    (filters.deviceIds && filters.deviceIds.length > 0) ||
    (filters.departments && filters.departments.length > 0) ||
    (filters.categoryIds && filters.categoryIds.length > 0) ||
    filters.createdDateFrom ||
    filters.createdDateTo ||
    filters.resolutionDateFrom ||
    filters.resolutionDateTo ||
    filters.daysOpenFrom ||
    filters.daysOpenTo
    // Free-text search is not an Active Filter (cleared via search box only)
    // Server default date window is not an Active Filter chip
  );
};

/**
 * Get user-friendly filter label for display
 *
 * @param {string} filterKey - Filter key name
 * @param {*} filterValue - Filter value
 * @returns {string} User-friendly label
 */
export const getComplaintFilterLabel = (filterKey, filterValue) => {
  const labelMap = {
    statuses: 'Status',
    severities: 'Severity',
    impactsAvailability: 'Availability Impact',
    filedByMe: 'Filed by me',
    locations: 'Location',
    deviceIds: 'Devices',
    departments: 'Departments',
    categoryIds: 'Category',
    createdDateFrom: 'Created Date (From)',
    createdDateTo: 'Created Date (To)',
    resolutionDateFrom: 'Resolved Date (From)',
    resolutionDateTo: 'Resolved Date (To)',
    daysOpenFrom: 'Days Open (Min)',
    daysOpenTo: 'Days Open (Max)',
    search: 'Search',
  };

  return labelMap[filterKey] || filterKey;
};

export default useComplaintFilterState;
